/* eslint-disable indent */
import React, { useEffect, useRef, useState } from 'react';
import { Control, Controller, useForm } from 'react-hook-form';
import { Button, Input, Radio, Spin, message } from 'antd';
import clsx from 'clsx';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { config } from '@/config';
import { trackAnalyticsEvent } from '@/core/analytics';
import { verificationTokenRepo } from '../adapters/repositories/verification-token.http.repository';
import {
  PASSWORDLESS_EVENTS,
  PasswordlessEngagedAction,
} from '../constants/passwordless-analytics.constants';
import { authService } from '../services/auth.service';
import {
  LoginSubject,
  PasswordlessVerifyPath,
  RequestLoginLinkOutcome,
  VerifyLoginOutcome,
  VerifyLoginOutput,
  VerifyLoginRefusalReason,
} from '../types/passwordless-login.types';
import {
  formatRetryAt,
  getVerifyRefusalAction,
  getVerifyRefusalMessageKey,
  isLoginIdentifierValid,
} from '../utils/passwordless-login';

type PasswordlessStep = 'request' | 'code' | 'selection' | 'verifying';

interface IdentifierFormValues {
  identifier: string;
}

interface CodeFormValues {
  code: string;
}

interface PasswordlessLoginPanelProps {
  initialLinkToken?: string | null;
  onAuthenticated: () => void;
  onBackToPassword: () => void;
  onClearLinkToken?: () => void;
  buttonStyle: React.CSSProperties;
  buttonBackStyle: React.CSSProperties;
  inputStyle: React.CSSProperties;
}

const identifierSchema = yup.object().shape({
  identifier: yup
    .string()
    .required('common.identifierRequired')
    .test('identifier', 'common.invalidIdentifier', (value) =>
      isLoginIdentifierValid(value || '', config.regex.email),
    ),
});

const codeSchema = yup.object().shape({
  code: yup.string().required('common.passwordlessCodeRequired'),
});

const PasswordlessLoginPanel: React.FC<PasswordlessLoginPanelProps> = ({
  initialLinkToken,
  onAuthenticated,
  onBackToPassword,
  onClearLinkToken,
  buttonStyle,
  buttonBackStyle,
  inputStyle,
}) => {
  const { t } = useTranslation();
  const linkTokenRef = useRef(initialLinkToken || '');
  const verifyStartedRef = useRef(false);

  const [step, setStep] = useState<PasswordlessStep>(initialLinkToken ? 'verifying' : 'request');
  const [busy, setBusy] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [requestId, setRequestId] = useState('');
  const [verifyPath, setVerifyPath] = useState<PasswordlessVerifyPath>(
    initialLinkToken ? 'link' : 'code',
  );
  const [subjects, setSubjects] = useState<LoginSubject[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>();
  const [inlineError, setInlineError] = useState('');

  const identifierForm = useForm<IdentifierFormValues>({
    resolver: yupResolver(identifierSchema),
    defaultValues: { identifier: '' },
  });

  const codeForm = useForm<CodeFormValues>({
    resolver: yupResolver(codeSchema),
    defaultValues: { code: '' },
  });

  const showRetryMessage = (key: string, retryAt?: string | null) => {
    const formatted = formatRetryAt(retryAt);
    const text = formatted ? t(key, { retryAt: formatted }) : t(key);
    setInlineError(text);
    message.error(text);
  };

  const goToRequestFresh = (reason: VerifyLoginRefusalReason, retryAt?: string | null) => {
    onClearLinkToken?.();
    linkTokenRef.current = '';
    setVerifyPath('code');
    setSubjects([]);
    setSelectedSubjectId(undefined);
    setStep('request');
    showRetryMessage(getVerifyRefusalMessageKey(reason), retryAt);
  };

  const completeAuthenticated = async (sessionToken: string, path: PasswordlessVerifyPath) => {
    await authService.signInWithSessionToken(sessionToken);
    trackAnalyticsEvent(PASSWORDLESS_EVENTS.LOGIN_COMPLETED, { path });
    message.success(t('common.approvedLoginSuccessfully'));
    onAuthenticated();
  };

  const leaveVerifyingSpinner = () => {
    if (step !== 'verifying') {
      return;
    }
    if (requestId) {
      setStep('code');
      return;
    }
    setStep('request');
    onClearLinkToken?.();
    linkTokenRef.current = '';
  };

  const applyVerifyResult = async (result: VerifyLoginOutput, path: PasswordlessVerifyPath) => {
    if (result.outcome === VerifyLoginOutcome.AUTHENTICATED) {
      const sessionToken = result.session?.sessionToken;
      if (!sessionToken) {
        setInlineError(t('common.passwordlessVerifyFailed'));
        message.error(t('common.passwordlessVerifyFailed'));
        return;
      }
      await completeAuthenticated(sessionToken, path);
      return;
    }

    if (result.outcome === VerifyLoginOutcome.SELECTION_REQUIRED) {
      const nextSubjects = result.subjects ?? [];
      setSubjects(nextSubjects);
      setSelectedSubjectId(nextSubjects[0]?.subjectId);
      setInlineError('');
      setStep('selection');
      return;
    }

    const reason = result.refusal?.reason ?? VerifyLoginRefusalReason.INVALID;
    const action = getVerifyRefusalAction(reason);
    const retryAt = result.refusal?.retryAt;

    if (action === 'request-fresh') {
      goToRequestFresh(reason, retryAt);
      return;
    }

    if (action === 'keep-code') {
      setStep('code');
      setInlineError(t(getVerifyRefusalMessageKey(reason)));
      return;
    }

    if (action === 'stay-picker') {
      setStep('selection');
      setInlineError(t(getVerifyRefusalMessageKey(reason)));
      return;
    }

    if (action === 'locked') {
      showRetryMessage(getVerifyRefusalMessageKey(reason), retryAt);
      leaveVerifyingSpinner();
      return;
    }

    setInlineError(t(getVerifyRefusalMessageKey(reason)));
    message.error(t(getVerifyRefusalMessageKey(reason)));
    leaveVerifyingSpinner();
  };

  const runVerify = async (path: PasswordlessVerifyPath, subjectId?: string) => {
    setVerifyPath(path);
    setBusy(true);
    setInlineError('');
    try {
      const result =
        path === 'link'
          ? await verificationTokenRepo.verifyLinkToken(linkTokenRef.current, subjectId)
          : await verificationTokenRepo.verifyCodeToken(
              requestId,
              codeForm.getValues('code'),
              subjectId,
            );
      await applyVerifyResult(result, path);
    } catch {
      message.error(t('common.passwordlessVerifyFailed'));
      setInlineError(t('common.passwordlessVerifyFailed'));
      if (path === 'link' && !subjectId) {
        setStep('request');
      }
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!initialLinkToken || verifyStartedRef.current) {
      return;
    }
    verifyStartedRef.current = true;
    void runVerify('link');
    // One-shot auto-verify of ?token=; verifyStartedRef prevents a second run.
  }, [initialLinkToken]);

  const trackEngaged = (action: PasswordlessEngagedAction) => {
    trackAnalyticsEvent(PASSWORDLESS_EVENTS.LOGIN_ENGAGED, { action });
  };

  const requestLink = async (values: IdentifierFormValues) => {
    setBusy(true);
    setInlineError('');
    const trimmed = values.identifier.trim();
    setIdentifier(trimmed);
    try {
      const result = await verificationTokenRepo.requestLink(trimmed);
      if (result.requestId) {
        setRequestId(result.requestId);
      }

      if (result.outcome === RequestLoginLinkOutcome.LOCKED) {
        showRetryMessage('common.passwordlessLocked', result.retryAt);
        return;
      }

      if (result.outcome === RequestLoginLinkOutcome.RESEND_TOO_SOON) {
        showRetryMessage('common.passwordlessResendTooSoon', result.retryAt);
        if (result.requestId) {
          setVerifyPath('code');
          setStep('code');
        }
        return;
      }

      setVerifyPath('code');
      setStep('code');
      message.success(t('common.passwordlessAcknowledged'));
    } catch {
      message.error(t('common.passwordlessRequestFailed'));
    } finally {
      setBusy(false);
    }
  };

  const onRequestLink = async (values: IdentifierFormValues) => {
    trackEngaged('send_code');
    await requestLink(values);
  };

  const onSubmitCode = async () => {
    trackEngaged('verify');
    await runVerify('code');
  };

  const onResend = async () => {
    if (!identifier) {
      setStep('request');
      return;
    }
    await requestLink({ identifier });
  };

  const onPickSubject = async () => {
    if (!selectedSubjectId) {
      setInlineError(t('common.passwordlessSelectAccount'));
      return;
    }
    await runVerify(verifyPath, selectedSubjectId);
  };

  const heading =
    step === 'selection'
      ? t('common.passwordlessSelectAccount')
      : step === 'code'
      ? t('common.passwordlessEnterCode')
      : t('common.loginWithEmailSmsCode');

  return (
    <div className='space-y-6'>
      <h2 className='text-xl font-semibold text-center'>{heading}</h2>
      {inlineError && <p className='text-red-500 text-sm text-center'>{inlineError}</p>}

      {step === 'verifying' && (
        <div className='flex flex-col items-center space-y-4 py-8'>
          <Spin />
          <p className='text-gray-600'>{t('common.passwordlessVerifyingLink')}</p>
        </div>
      )}

      {step === 'request' && (
        <form onSubmit={identifierForm.handleSubmit(onRequestLink)} className='space-y-6'>
          <IdentifierField
            control={identifierForm.control}
            error={identifierForm.formState.errors.identifier?.message}
            inputStyle={inputStyle}
          />
          <Button
            type='primary'
            htmlType='submit'
            className='w-full'
            style={buttonStyle}
            loading={busy}
          >
            {t('common.passwordlessSendCode')}
          </Button>
          <div className='text-center'>
            <button
              type='button'
              className='text-blue-500 text-sm bg-transparent border-none cursor-pointer'
              onClick={onBackToPassword}
            >
              {t('common.usePasswordInstead')}
            </button>
          </div>
        </form>
      )}

      {step === 'code' && (
        <form onSubmit={codeForm.handleSubmit(onSubmitCode)} className='space-y-6'>
          <p className='text-sm text-gray-600 text-center'>
            {t('common.passwordlessAcknowledged')}
          </p>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>
              {t('common.passwordlessCode')}(*)
            </label>
            <Controller
              name='code'
              control={codeForm.control}
              render={({ field }) => (
                <Input
                  {...field}
                  style={inputStyle}
                  placeholder={t('common.passwordlessEnterCode')}
                  autoComplete='one-time-code'
                />
              )}
            />
            {codeForm.formState.errors.code && (
              <p className='text-red-500 text-sm mt-1'>
                {t(codeForm.formState.errors.code.message as string)}
              </p>
            )}
          </div>
          <Button
            type='primary'
            htmlType='submit'
            className='w-full'
            style={buttonStyle}
            loading={busy}
          >
            {t('common.passwordlessVerifyCode')}
          </Button>
          <div className='flex justify-between'>
            <button
              type='button'
              className='text-blue-500 text-sm bg-transparent border-none cursor-pointer'
              onClick={() => {
                setInlineError('');
                setStep('request');
              }}
            >
              {t('common.passwordlessBackToRequest')}
            </button>
            <button
              type='button'
              className='text-blue-500 text-sm bg-transparent border-none cursor-pointer'
              onClick={onResend}
              disabled={busy}
            >
              {t('common.passwordlessResendCode')}
            </button>
          </div>
        </form>
      )}

      {step === 'selection' && (
        <div className='space-y-6'>
          <p className='text-sm text-gray-600 text-center'>
            {t('common.passwordlessSelectAccountHint')}
          </p>
          <Radio.Group
            value={selectedSubjectId}
            onChange={(event) => setSelectedSubjectId(event.target.value)}
            className='w-full'
          >
            <div className='flex flex-col gap-3 w-full'>
              {subjects.map((subject) => (
                <Radio
                  key={subject.subjectId}
                  value={subject.subjectId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    marginInlineEnd: 0,
                  }}
                  className={clsx(
                    'box-border px-4 py-3 rounded-lg border border-solid shadow-sm cursor-pointer transition-all duration-150 hover:shadow-md',
                    selectedSubjectId === subject.subjectId
                      ? 'border-[#cc1e1e] bg-[#fdf2f2]'
                      : 'border-gray-300 bg-white hover:border-[#cc1e1e]',
                  )}
                >
                  <span className='text-sm font-medium text-gray-800'>{subject.displayName}</span>
                </Radio>
              ))}
            </div>
          </Radio.Group>
          <div className='flex space-x-4'>
            <Button
              className='flex-1'
              style={buttonBackStyle}
              onClick={() => {
                setInlineError('');
                setStep(verifyPath === 'code' ? 'code' : 'request');
              }}
            >
              {t('common.passwordlessBackToRequest')}
            </Button>
            <Button
              type='primary'
              className='flex-1'
              style={buttonStyle}
              loading={busy}
              onClick={onPickSubject}
            >
              {t('common.login')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const IdentifierField: React.FC<{
  control: Control<IdentifierFormValues>;
  error?: string;
  inputStyle: React.CSSProperties;
}> = ({ control, error, inputStyle }) => {
  const { t } = useTranslation();
  return (
    <div>
      <label className='block text-sm font-medium text-gray-700 mb-1'>
        {t('common.emailOrPhone')}(*)
      </label>
      <Controller
        name='identifier'
        control={control}
        render={({ field }) => (
          <Input {...field} style={inputStyle} placeholder={t('common.enterEmailOrPhone')} />
        )}
      />
      {error && <p className='text-red-500 text-sm mt-1'>{t(error)}</p>}
    </div>
  );
};

export default PasswordlessLoginPanel;
